import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import moment from "moment";
import { toast } from "react-toastify";
import {
  Container,
  Dimmer,
  Header,
  Icon,
  Loader,
  Segment,
  Table,
} from "semantic-ui-react";
import * as ACTIVATION_RULES from "../../../actions/activationRulesActions";

// Constants
const LOG_EXPIRY_DAYS = 60;
const DATE_FORMAT = "DD-MM-YYYY hh:mm:ss a";

// Utility Functions
const updateState = (setState, updates) => {
  setState(prevState => ({ ...prevState, ...updates }));
};

const parseEventMessage = (event) => JSON.parse(event.message);

const formatDateTime = (dateTime) => moment.utc(dateTime).format(DATE_FORMAT);

const combineAndSortEvents = (invocationEvents, successEvents) => {
  const eventMap = {};

  // Process invocation events
  invocationEvents.forEach((event) => {
    const parsedEvent = parseEventMessage(event);
    eventMap[parsedEvent.invocationTime] = {
      ...parsedEvent,
      success: false,
    };
  });

  // Process success events
  successEvents.forEach((event) => {
    const parsedEvent = parseEventMessage(event);
    if (parsedEvent.invocationTime in eventMap) {
      eventMap[parsedEvent.invocationTime] = {
        ...eventMap[parsedEvent.invocationTime],
        ...parsedEvent,
        success: true,
      };
    } else {
      eventMap[parsedEvent.invocationTime] = {
        ...parsedEvent,
        success: true,
      };
    }
  });

  // Sort by invocation time (descending)
  const sortedKeys = Object.keys(eventMap).sort((a, b) => b.localeCompare(a));
  return sortedKeys.map(key => eventMap[key]);
};

const ActivationRuleLogDetails = (props) => {
  const [state, setState] = useState({
    loading: false,
  });

  useEffect(() => {
    const fetchLogs = async () => {
      updateState(setState, { loading: true });

      try {
        await props.fetchActivationRuleLogsAction(props.activationRule);
        updateState(setState, { loading: false });
      } catch (error) {
        updateState(setState, { loading: false });
        toast.error("Failed to fetch activation rule logs");
      }
    };

    fetchLogs();
  }, [props.activationRule, props.fetchActivationRuleLogsAction]);

  const renderLogsTable = (combinedLogs) => (
    <Segment>
      <Table size="large">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell className="nectaPrimaryBg">Date Invoked</Table.HeaderCell>
            <Table.HeaderCell className="nectaPrimaryBg">WhatsApp Users</Table.HeaderCell>
            <Table.HeaderCell className="nectaPrimaryBg">Mobile Users</Table.HeaderCell>
            <Table.HeaderCell className="nectaPrimaryBg">Total Users</Table.HeaderCell>
            <Table.HeaderCell className="nectaPrimaryBg">Maximum Users</Table.HeaderCell>
            <Table.HeaderCell className="nectaPrimaryBg">Status</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {combinedLogs.map((log, index) => (
            <Table.Row key={`${log.ruleId}.${index}`}>
              <Table.Cell>{formatDateTime(log.invocationTime)}</Table.Cell>
              <Table.Cell>{log.whatsappUsers || 0}</Table.Cell>
              <Table.Cell>{log.mobileUsers || 0}</Table.Cell>
              <Table.Cell>{(log.mobileUsers + log.whatsappUsers) || 0}</Table.Cell>
              <Table.Cell>{log.totalUsers}</Table.Cell>
              <Table.Cell>{(log.status || '').toUpperCase()}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <p style={{ marginTop: "10px", fontSize: "0.85em", color: "red" }}>
        Note: Logs expire after {LOG_EXPIRY_DAYS} days.
      </p>
    </Segment>
  );

  const renderEmptyState = () => (
    <Segment>
      <Header icon>
        <Icon name="file" color="black" />
        Logs Unavailable
      </Header>
      <p style={{ marginTop: "10px", fontSize: "0.85em", color: "red" }}>
        Note: Logs expire after {LOG_EXPIRY_DAYS} days.
      </p>
    </Segment>
  );

  const renderContent = () => {
    if (state.loading) {
      return (
        <Dimmer active inverted>
          <Loader>Loading...</Loader>
        </Dimmer>
      );
    }

    const combinedLogs = combineAndSortEvents(props.invocationEvents, props.successEvents);

    return combinedLogs.length > 0
      ? renderLogsTable(combinedLogs)
      : renderEmptyState();
  };

  return <Container>{renderContent()}</Container>;
};

const mapStateToProps = (state, ownProps) => ({
  tenant: state.tenantManagement.activeTenant,
  activationRule: ownProps.activationRule,
  invocationEvents: _.isEmpty(state.activationRules.ruleLogs.ruleLogsInvocations)
    ? []
    : Object.values(state.activationRules.ruleLogs.ruleLogsInvocations),
  successEvents: _.isEmpty(state.activationRules.ruleLogs.ruleLogsSuccesses)
    ? []
    : Object.values(state.activationRules.ruleLogs.ruleLogsSuccesses),
});

export default connect(
  mapStateToProps,
  (dispatch) => ({
    fetchActivationRuleLogsAction: (activationRule) =>
      dispatch(ACTIVATION_RULES.fetchActivationRuleLogsAction(activationRule)),
  })
)(ActivationRuleLogDetails);
